import { useAuthContext } from '@/auth/useAuthContext';
import {
  REPORT_CARD_BOOKING_TYPES,
  type ReportCardView,
  formatReportCardDate,
  reportCardThumbUrl,
} from '@/lib/report_cards';
import { ClipboardList, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ReportCardComposer } from './ReportCardComposer';

/**
 * Staff-side report cards panel (pet report cards, migration 0093).
 *
 * Lists every card sent for a booking, newest-first, and exposes the
 * "Send report card" action to staff holding `report_cards.send`. Renders
 * nothing for grooming / service bookings (cards are a boarding + daycare
 * feature). Mounted on the booking detail page next to the live-stay panel.
 */

const SECTION_LABEL = 'text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground';

export interface ReportCardsPanelProps {
  bookingId: string;
  bookingType: string;
  petName?: string | null;
}

export function ReportCardsPanel({ bookingId, bookingType, petName }: ReportCardsPanelProps) {
  const { state: authState } = useAuthContext();
  const canSend = authState.me?.capabilities?.includes('report_cards.send') ?? false;
  const [cards, setCards] = useState<ReportCardView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const applicable = REPORT_CARD_BOOKING_TYPES.has(bookingType);

  useEffect(() => {
    if (!applicable) return;
    let cancelled = false;
    fetch(`/api/bookings/${encodeURIComponent(bookingId)}/report-cards`, {
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Could not load report cards (${res.status})`);
        return (await res.json()) as { reportCards: ReportCardView[] };
      })
      .then((body) => {
        if (!cancelled) setCards(body.reportCards ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId, applicable]);

  if (!applicable) return null;

  return (
    <div data-testid="report-cards-panel">
      <div className="mb-2 flex items-center justify-between">
        <p className={SECTION_LABEL}>Report cards</p>
        {canSend && (
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-secondary"
          >
            <Send className="size-3.5" strokeWidth={1.75} />
            Send report card
          </button>
        )}
      </div>

      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
      {cards === null && !error && <p className="text-xs text-muted-foreground">Loading…</p>}
      {cards !== null && cards.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="report-cards-empty">
          No report cards sent yet.
        </p>
      )}
      {cards !== null && cards.length > 0 && (
        <ul className="space-y-2">
          {cards.map((card) => (
            <li
              key={card.id}
              className="rounded-md border border-border p-3"
              data-testid={`report-card-${card.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                  <ClipboardList className="size-3.5 text-muted-foreground" strokeWidth={1.75} />
                  {card.moodLabel ?? 'Report card'}
                </span>
                <span className="num text-xs text-muted-foreground">
                  {card.sentAt ? formatReportCardDate(card.sentAt) : card.reportDate}
                </span>
              </div>
              {card.notes && (
                <p className="mt-1 line-clamp-3 whitespace-pre-line text-xs text-muted-foreground">
                  {card.notes}
                </p>
              )}
              {card.photos.length > 0 && (
                <div className="mt-2 flex gap-1.5">
                  {card.photos.slice(0, 6).map((p) => (
                    <img
                      key={p.photoId}
                      src={reportCardThumbUrl(p.fileId)}
                      alt={p.caption ?? ''}
                      loading="lazy"
                      className="size-10 rounded bg-muted object-cover"
                    />
                  ))}
                  {card.photos.length > 6 && (
                    <span className="num self-center text-xs text-muted-foreground">
                      +{card.photos.length - 6}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {composerOpen && (
        <ReportCardComposer
          bookingId={bookingId}
          petName={petName ?? null}
          onClose={() => setComposerOpen(false)}
          onSent={(card) => setCards((prev) => [card, ...(prev ?? [])])}
        />
      )}
    </div>
  );
}

/**
 * Compact "Send report card" affordance for the Today board's occupant
 * drawer. Capability-gated; opens the composer in place.
 */
export function SendReportCardButton({
  bookingId,
  petName,
  className,
}: {
  bookingId: string;
  petName?: string | null;
  className?: string;
}) {
  const { state: authState } = useAuthContext();
  const canSend = authState.me?.capabilities?.includes('report_cards.send') ?? false;
  const [open, setOpen] = useState(false);
  if (!canSend) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          'inline-flex items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted'
        }
      >
        <Send className="size-4" strokeWidth={1.75} />
        Send report card
      </button>
      {open && (
        <ReportCardComposer
          bookingId={bookingId}
          petName={petName ?? null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
